import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useOverrideRequirements } from "../../hooks/v2";
import { api } from "../../lib/api";
import type { OverrideAction } from "../../lib/typesV2";
import { Modal } from "../ui/Modal";

interface Props {
  open: boolean;
  action: OverrideAction;
  /** What the cashier is trying to do (printed on the audit event). */
  entity?: { type?: string; id?: string; amount?: number };
  onClose: () => void;
  /** Called with the one-time approval id; the caller sends it with the protected request (`overrideId`). */
  onApproved: (overrideId: string, approverName: string) => void;
}

/**
 * Manager approval. Which fields appear is decided by the branch's switches: PIN only, badge only, both (strict 2FA),
 * or a notice that overrides are switched off. A badge reader typing into the badge box (keyboard wedge) works as-is.
 */
export function OverrideDialog({ open, action, entity, onClose, onApproved }: Props) {
  const { t } = useTranslation();
  const { toastError } = useToast();
  const req = useOverrideRequirements(open);
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [badge, setBadge] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setUsername(""); setPin(""); setBadge(""); setReason(""); }
  }, [open]);

  const r = req.data;
  const needsName = !!r && !r.requiresRfid; // PIN-only: the approver is named; with a badge the badge identifies them
  const ready = !!r?.enabled && (!r.requiresPin || pin.length >= 4) && (!r.requiresRfid || badge.length > 0) && (!needsName || username.trim().length > 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!r || !ready) return;
    setBusy(true);
    try {
      const out = await api.post<{ overrideId: string; approverName: string }>("/overrides/authorize", {
        action, reason: reason.trim() || undefined, entityType: entity?.type, entityId: entity?.id, amount: entity?.amount,
        approver: { username: username.trim() || undefined, pin: r.requiresPin ? pin : undefined, badgeUid: r.requiresRfid ? badge.trim() : undefined },
      });
      onApproved(out.overrideId, out.approverName);
    } catch (err) {
      toastError(err);
      setPin(""); setBadge("");
    } finally {
      setBusy(false);
    }
  }

  const factors = r ? [r.requiresPin && t("override.factorPin"), r.requiresRfid && t("override.factorBadge")].filter(Boolean).join(" + ") : "";
  return (
    <Modal open={open} title={t("override.title")} onClose={onClose} size="sm">
      <form onSubmit={submit} className="space-y-3" aria-label={t("override.title")}>
        <p className="text-xs font-semibold text-gray-600">{t("override.subtitle", { action: t(`override.action.${action}`) })}</p>
        {r && !r.enabled ? (
          <p role="alert" className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-800">{t("override.disabled")}</p>
        ) : (
          r && (
            <>
              <p className="text-[11px] font-bold text-gray-500">{t("override.needs", { factors })}</p>
              {needsName && (
                <div>
                  <label className="label" htmlFor="ov-user">{t("override.username")}</label>
                  <input id="ov-user" className="field" autoFocus autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
                </div>
              )}
              {r.requiresRfid && (
                <div>
                  <label className="label" htmlFor="ov-badge">{t("override.badge")}</label>
                  <input id="ov-badge" className="field num font-mono" autoFocus autoComplete="off" value={badge} onChange={(e) => setBadge(e.target.value)} />
                </div>
              )}
              {r.requiresPin && (
                <div>
                  <label className="label" htmlFor="ov-pin">{t("override.pin")}</label>
                  <input id="ov-pin" className="field num font-mono" type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
                </div>
              )}
              <div>
                <label className="label" htmlFor="ov-reason">{t("override.reason")}</label>
                <input id="ov-reason" className="field" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
              </div>
            </>
          )
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={!ready || busy}>{busy ? t("common.loading") : t("override.submit")}</button>
        </div>
      </form>
    </Modal>
  );
}
